import { InputTextField } from '@abbott-components/core';
import JSONSchema7Properties from '../../Shared/jSONSchema7Properties';
import Stack from '@mui/material/Stack';
import ControlState from '../../Shared/controlState';
import { ChangeEvent, useContext, useEffect, useRef, useState } from 'react';
import { WorkflowEngineContext } from '../../WorkflowEngine/workflowEngineReducer';
import { JSONSchema7 } from 'json-schema';
import Button from '@mui/material/Button';
import { useFormContext } from 'react-hook-form';
import Typography from '@mui/material/Typography';
import convertFileToDataURLAsync from '../../Shared/convertFileToDataURLAsync';
import { getAttachmentUrl } from '../../Shared/ajax';
import config from '../../Shared/config';
import formatName from '../formatName';
import {
  isValidFileSignature,
  isValidAttachmentExtension
} from '../../Shared/fileTypeValidation';
import SizingSelect from '../../Shared/SizingSelect';

export default (control: ControlState) => {
  const { state } = useContext(WorkflowEngineContext);

  const { isReadOnlyView, versionExternalId } = state;

  const { schema } = control;

  const properties = schema?.properties as JSONSchema7Properties;

  const labelSchema = properties['label'] as JSONSchema7;
  const displayNameSchema = properties['displayName'] as JSONSchema7;
  const idSchema = properties['id'] as JSONSchema7;
  const externalIdSchema = properties['externalId'] as JSONSchema7;
  const dataSchema = properties['data'] as JSONSchema7;
  const filenameSchema = properties['filename'] as JSONSchema7;
  const mimeTypeSchema = properties['mimeType'] as JSONSchema7;
  const gridSizeSchema = properties['gridSize'] as JSONSchema7;

  const fieldLabel = formatName(labelSchema.$id as string);
  const fieldDisplayName = formatName(displayNameSchema.$id as string);
  const fieldId = formatName(idSchema.$id as string);
  const fieldExternalId = formatName(externalIdSchema.$id as string);
  const fieldData = formatName(dataSchema.$id as string);
  const fieldFilename = formatName(filenameSchema.$id as string);
  const fieldMimeType = formatName(mimeTypeSchema.$id as string);
  const gridSize = formatName(gridSizeSchema.$id as string);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const controlWrapperRef = useRef<HTMLDivElement | null>(null);

  const [filename, setFilename] = useState<string>('');

  const [fileLimitError, setFileLimitError] = useState<string>('');
  const [fileSignatureError, setFileSignatureError] = useState<string>('');
  const [fileExtensionError, setFileExtensionError] = useState<string>('');

  const fileTypes = config.supportedAttachmentMimeTypes;

  const formContext = useFormContext();

  const fieldFilenameState = formContext?.getFieldState(fieldFilename);

  const handleFileUploadChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const fileUpload: File | undefined =
      e.currentTarget.files && e.currentTarget.files.length
        ? (e.currentTarget.files[0] as File)
        : undefined;

    if (!fileUpload) return;

    formContext?.clearErrors();

    const sizeInMB = fileUpload.size / 1000000;

    if (sizeInMB > config.attachmentSizeLimitInMB) {
      setFileLimitError(
        `File must be no bigger than ${config.attachmentSizeLimitInMB} MB.`
      );
      return;
    }

    const validFileSignature = await isValidFileSignature(fileUpload);

    if (!validFileSignature) {
      setFileSignatureError(
        'File signature does not match supported file types.'
      );
      return;
    }

    if (!isValidAttachmentExtension(fileUpload.name)) {
      setFileExtensionError('File extension is not of a supported file type.');
      return;
    }

    const attachmentDataURL = await convertFileToDataURLAsync(fileUpload);

    formContext?.setValue(fieldData, attachmentDataURL);
    formContext?.setValue(fieldFilename, fileUpload.name);
    formContext?.setValue(fieldMimeType, fileUpload.type);

    setFilename(fileUpload.name);
    setFileLimitError('');
    setFileSignatureError('');
    setFileExtensionError('');

    formContext?.clearErrors();
  };

  const handleClearClick = () => {
    if (fileInputRef.current !== null) {
      fileInputRef.current.value = '';
    }

    formContext?.setValue(fieldData, '');
    formContext?.setValue(fieldFilename, '');
    formContext?.setValue(fieldMimeType, '');

    setFilename('');
    setFileLimitError('');
    setFileSignatureError('');
    setFileExtensionError('');

    formContext?.clearErrors();
  };

  const renderValidationErrors = () => {
    let errorMsg = '';

    if (fieldFilenameState?.error) {
      errorMsg = fieldFilenameState?.error?.message as string;
    }

    return errorMsg !== '' ? (
      <Typography sx={{ marginTop: 2, color: 'abbott.red' }}>
        {errorMsg}
      </Typography>
    ) : null;
  };

  const renderFileLimitError = () =>
    fileLimitError && (
      <Typography sx={{ marginTop: 2, color: 'abbott.red' }}>
        {fileLimitError}
      </Typography>
    );

  const renderFileSignatureError = () =>
    fileSignatureError && (
      <Typography sx={{ marginTop: 2, color: 'abbott.red' }}>
        {fileSignatureError}
      </Typography>
    );

  const renderFileExtensionError = () =>
    fileExtensionError && (
      <Typography sx={{ marginTop: 2, color: 'abbott.red' }}>
        {fileExtensionError}
      </Typography>
    );

  const renderHiddenField = () => (
    <Stack sx={{ display: 'none' }}>
      {/* Hidden field used to capture file upload value in form */}
      <InputTextField name={fieldData} />
      <InputTextField name={fieldExternalId} />
      <InputTextField name={fieldFilename} />
      <InputTextField name={fieldMimeType} />
    </Stack>
  );

  useEffect(() => {
    const fileData = formContext?.getValues(fieldData);
    const attachmentDataURL = formContext?.getValues(fieldExternalId);
    const filename = formContext?.getValues(fieldFilename);
    const fileDataURL = attachmentDataURL
      ? getAttachmentUrl(versionExternalId, attachmentDataURL)
      : fileData;

    if (!fileDataURL) return;

    setFilename(filename);
  }, []);

  return (
    <Stack ref={controlWrapperRef} direction="column" spacing={3}>
      {renderHiddenField()}
      <Stack direction="row" spacing={3}>
        <Stack sx={{ flexGrow: 4 }}>
          <InputTextField
            name={fieldLabel}
            label="Heading (optional)"
            isDisabled={isReadOnlyView}
          />
        </Stack>
        <Stack sx={{ flexGrow: 4 }}>
          <InputTextField
            name={fieldDisplayName}
            label="Display Name"
            isDisabled={isReadOnlyView}
          />
        </Stack>
        <Stack sx={{ flexGrow: 4 }}>
          <InputTextField
            name={fieldId}
            label="ID"
            isDisabled={isReadOnlyView}
          />
        </Stack>
        <SizingSelect isReadOnlyView={isReadOnlyView} name={gridSize} />
      </Stack>
      <Stack direction="row" spacing={2}>
        <Stack direction="row">
          <Button
            variant="contained"
            component="label"
            disabled={isReadOnlyView}
          >
            {'Upload'}
            <input
              ref={fileInputRef}
              hidden
              accept={fileTypes.join(',')}
              type="file"
              onChange={handleFileUploadChange}
            />
          </Button>
          <Button
            variant="text"
            component="label"
            disabled={isReadOnlyView}
            onClick={handleClearClick}
          >
            {'Clear'}
          </Button>
        </Stack>
        {filename && (
          <Stack>
            <Typography variant="caption">{filename}</Typography>
          </Stack>
        )}
      </Stack>
      {renderValidationErrors()}
      {renderFileLimitError()}
      {renderFileSignatureError()}
      {renderFileExtensionError()}
    </Stack>
  );
};
